import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Input from "@/components/ui/input";
import Button from "@/components/ui/button";
import { Plus as PlusIcon } from "@/components/icons";
import LibraryShareCustomFields from "../LibraryShareCustomFields";
import "./index.scss";

export type ShareOption = "everyone" | "admins" | "custom";

export interface AddVideoLinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (payload: {
    name: string;
    url: string;
    share: ShareOption;
    sharedUserIds?: string[];
    sharedDepartmentIds?: string[];
    sharedDepartmentTypes?: number[];
  }) => void | Promise<void>;
}

const YOUTUBE_HOSTS: Record<string, true> = {
  "youtube.com": true,
  "www.youtube.com": true,
  "m.youtube.com": true,
  "youtu.be": true,
};

function isYoutubeUrl(value: string): boolean {
  try {
    const url = new URL(value.trim());
    return ["http:", "https:"].includes(url.protocol) && YOUTUBE_HOSTS[url.hostname] === true;
  } catch {
    return false;
  }
}

const AddVideoLinkModal: React.FC<AddVideoLinkModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [urlTouched, setUrlTouched] = useState(false);
  const [share, setShare] = useState<ShareOption>("everyone");
  const [sharedUserIds, setSharedUserIds] = useState<string[]>([]);
  const [sharedDepartmentIds, setSharedDepartmentIds] = useState<string[]>([]);
  const [sharedDepartmentTypes, setSharedDepartmentTypes] = useState<string[]>([]);

  const resetForm = () => {
    setName("");
    setUrl("");
    setUrlTouched(false);
    setShare("everyone");
    setSharedUserIds([]);
    setSharedDepartmentIds([]);
    setSharedDepartmentTypes([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isUrlValid = url.trim().length > 0 && isYoutubeUrl(url);
  const isFormValid = name.trim().length > 0 && isUrlValid;

  const handleSubmit = async () => {
    setUrlTouched(true);
    if (!isFormValid) return;
    await onSubmit?.({
      name: name.trim(),
      url: url.trim(),
      share,
      sharedUserIds: share === "custom" ? sharedUserIds : undefined,
      sharedDepartmentIds: share === "custom" ? sharedDepartmentIds : undefined,
      sharedDepartmentTypes:
        share === "custom"
          ? sharedDepartmentTypes.map((type) => Number(type)).filter(Number.isFinite)
          : undefined,
    });
    handleClose();
  };

  const customHeader = (
    <div className="add-video-link-modal-header">
      <div className="add-video-link-modal-header-left">
        <div className="add-video-link-modal-icon">
          <PlusIcon width={20} height={20} />
        </div>
        <h2>{t("library.addVideoLinkModal.title")}</h2>
      </div>
      <button
        type="button"
        className="add-video-link-modal-close"
        onClick={handleClose}
        aria-label={t("buttons.cancel")}
      >
        ×
      </button>
    </div>
  );

  const customFooter = (
    <div className="add-video-link-modal-footer">
      <Button variant="text" color="secondary" onClick={handleClose}>
        {t("library.addVideoLinkModal.cancel")}
      </Button>
      <Button variant="contained" color="primary" onClick={handleSubmit} disabled={!isFormValid}>
        {t("library.addVideoLinkModal.addButton")}
      </Button>
    </div>
  );

  return (
    <Modal
      title={t("library.addVideoLinkModal.title")}
      isOpen={isOpen}
      onClose={handleClose}
      customHeader={customHeader}
      customFooter={customFooter}
      size="md"
    >
      <div className="add-video-link-modal-content">
        <div className="add-video-link-modal-field">
          <Input
            label={t("library.addVideoLinkModal.name")}
            placeholder={t("library.addVideoLinkModal.namePlaceholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="add-video-link-modal-field">
          <Input
            label={t("library.addVideoLinkModal.url")}
            placeholder={t("library.addVideoLinkModal.urlPlaceholder")}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onBlur={() => setUrlTouched(true)}
            error={urlTouched && url.trim().length > 0 && !isUrlValid}
            errorMessage={t("library.addVideoLinkModal.invalidUrl")}
            required
          />
        </div>

        <div className="add-video-link-modal-share">
          <h3 className="add-video-link-modal-share-title">
            {t("library.addVideoLinkModal.share")}
          </h3>
          <div className="add-video-link-modal-share-options">
            {(["everyone", "admins", "custom"] as ShareOption[]).map((option) => (
              <label
                key={option}
                className={`add-video-link-modal-radio-option ${share === option ? "selected" : ""}`}
              >
                <input
                  type="radio"
                  name="video-link-share"
                  value={option}
                  checked={share === option}
                  onChange={() => setShare(option)}
                />
                <div className="add-video-link-modal-radio-text">
                  <span className="add-video-link-modal-radio-label">
                    {t(`library.addVideoLinkModal.${option}`)}
                  </span>
                  <span className="add-video-link-modal-radio-desc">
                    {t(`library.addVideoLinkModal.${option}Desc`)}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </div>

        {share === "custom" && (
          <LibraryShareCustomFields
            sharedUserIds={sharedUserIds}
            sharedDepartmentIds={sharedDepartmentIds}
            sharedDepartmentTypes={sharedDepartmentTypes}
            onUsersChange={setSharedUserIds}
            onDepartmentsChange={setSharedDepartmentIds}
            onDepartmentTypesChange={setSharedDepartmentTypes}
            showDepartmentTypes
            isVisible={isOpen}
          />
        )}
      </div>
    </Modal>
  );
};

export default AddVideoLinkModal;
